import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/Button'
import { optimizationApi } from '../../services/optimizationApi'
import { useOptimizationRunLive } from '../../hooks/useOptimizationRunLive'
import ReplanificationForm, { ReplanificationData } from '../../components/optimization/ReplanificationForm'
import PlacementDiff from '../../components/optimization/PlacementDiff'
import { computeLoadedCartonsCount, computeTotalCartonsCount } from '../../lib/optimizationUtils'
import { opt } from '../../lib/optimizationStyles'
import { useAuth } from '../../hooks/useAuth'

export default function OptimizationReplan() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showDiff, setShowDiff] = useState(false)

  if (!runId) {
    return <div className="p-4 text-red-600 dark:text-red-400">ID non valide</div>
  }

  const { run: previousRun, loading, error } = useOptimizationRunLive({
    runId,
    enabled: true,
  })

  const replanMutation = useMutation({
    mutationFn: async (data: ReplanificationData) => {
      const newInput = {
        ...previousRun!.input,
        tenantId: user?.tenantId || previousRun!.input.tenantId || previousRun!.tenantId || previousRun!.tenant_id || null,
        jobId: previousRun!.input.jobId || previousRun!.jobId || previousRun!.job_id || null,
        items: previousRun!.input.items.filter((item: any) => !data.excluded_item_ids?.includes(item.id)),
      }

      // Ensure the stable job_id is passed to the backend
      if (!newInput.jobId && previousRun!.job_id) {
        newInput.jobId = previousRun!.job_id
      }

      if (data.added_items && data.added_items.length > 0) {
        newInput.items = [...newInput.items, ...data.added_items]
      }

      // Use the dedicated /replan endpoint to benefit from backend diff logic,
      // causation_id tracking, and plan_version increment
      return optimizationApi.replanRun(runId, {
        previousRun: previousRun?.result || null,
        input: newInput,
        options: {
          incident_type: data.incident_type,
          incident_description: data.incident_description,
          reason: data.reason,
        },
      })
    },
    onSuccess: (response) => {
      const newRunId = response.run?.id
      if (newRunId) {
        navigate(`/optimization/${newRunId}`)
      }
    },
  })

  if (loading) {
    return (
      <div className={opt.loadingCenter}>
        <div className="text-center">
          <div className={`${opt.spinner} mb-4`} />
          <p className={opt.loadingText}>Chargement de l&apos;optimisation...</p>
        </div>
      </div>
    )
  }

  if (error || !previousRun) {
    return (
      <div className={opt.page}>
        <div className={opt.content}>
          <button type="button" onClick={() => navigate('/optimization')} className={opt.backBtn}>
            <ChevronLeftIcon className="w-4 h-4" />
            Retour
          </button>
          <div className={opt.alertErrorBox}>
            <p className="font-semibold mb-2">Erreur</p>
            <p>{error?.message || "L'optimisation n'a pas pu être trouvée"}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!previousRun.result) {
    return (
      <div className={opt.page}>
        <div className={opt.content}>
          <button type="button" onClick={() => navigate(`/optimization/${runId}`)} className={opt.backBtn}>
            <ChevronLeftIcon className="w-4 h-4" />
            Retour au détail
          </button>
          <div className={opt.alertWarnBox}>
            <p className="font-semibold mb-2">Résultats non disponibles</p>
            <p>Cette optimisation ne peut pas être replanifiée car elle n&apos;a pas encore de résultats.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={opt.page}>
      <div className={opt.pageHeader}>
        <div className={opt.headerInnerMd}>
          <button type="button" onClick={() => navigate(`/optimization/${runId}`)} className={opt.backBtn}>
            <ChevronLeftIcon className="w-4 h-4" />
            Retour au détail
          </button>
          <h1 className={`${opt.h1} mb-2`}>Replanification</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Optimisation #{previousRun.id} — Créer une nouvelle planification
          </p>
        </div>
      </div>

      <div className={opt.content}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className={opt.cardP6}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Paramètres de Replanification
              </h2>
              <ReplanificationForm
                previousRun={previousRun}
                onSubmit={(data) => replanMutation.mutate(data)}
                isLoading={replanMutation.isPending}
              />
            </div>
          </div>

          <div className="space-y-6">
            <div className={opt.cardP6}>
              <h3 className={opt.h3Lg}>Optimisation Précédente</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className={opt.dt}>ID</dt>
                  <dd className="font-mono font-medium text-gray-900 dark:text-white">{previousRun.id}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Statut</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{previousRun.status}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Colis chargés</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {computeLoadedCartonsCount(previousRun)}/{computeTotalCartonsCount(previousRun)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Taux volume</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {((previousRun.result?.fill_rate_volume || 0) * 100).toFixed(1)}%
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Score</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {((previousRun.result?.global_score || 0) * 100).toFixed(1)}/100
                  </dd>
                </div>
              </dl>
            </div>

            <div className={opt.cardP6}>
              <h3 className={opt.h3Lg}>Statistiques chargement</h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className={`${opt.dt} mb-1`}>Taux poids</dt>
                  <dd className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {((previousRun.result?.fill_rate_weight || 0) * 100).toFixed(1)}%
                  </dd>
                </div>
                <div>
                  <dt className={`${opt.dt} mb-1`}>Validité</dt>
                  <dd
                    className={`font-semibold ${previousRun.result?.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}
                  >
                    {previousRun.result?.valid ? 'Valide' : 'Invalide'}
                  </dd>
                </div>
                <div>
                  <dt className={`${opt.dt} mb-1`}>Violations</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {previousRun.result?.violations?.length || 0}
                  </dd>
                </div>
                <div>
                  <dt className={`${opt.dt} mb-1`}>Temps exécution</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">
                    {(previousRun.result?.execution_time_ms || 0).toFixed(0)} ms
                  </dd>
                </div>
              </dl>
            </div>

            <div className={opt.cardP6}>
              <h3 className={opt.h3Lg}>Espace de chargement</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className={opt.dt}>Longueur</dt>
                  <dd className="font-mono text-gray-900 dark:text-white">{previousRun.input.loadSpace.length} mm</dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Largeur</dt>
                  <dd className="font-mono text-gray-900 dark:text-white">{previousRun.input.loadSpace.width} mm</dd>
                </div>
                <div className="flex justify-between">
                  <dt className={opt.dt}>Hauteur</dt>
                  <dd className="font-mono text-gray-900 dark:text-white">{previousRun.input.loadSpace.height} mm</dd>
                </div>
                {previousRun.input.constraints?.maxWeight != null && (
                  <div className="flex justify-between">
                    <dt className={opt.dt}>Poids max</dt>
                    <dd className="font-mono text-gray-900 dark:text-white">
                      {previousRun.input.constraints.maxWeight} kg
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            <Button
              type="button"
              onClick={() => setShowDiff(!showDiff)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {showDiff ? 'Masquer la comparaison' : 'Afficher la comparaison'}
            </Button>
          </div>
        </div>

        {showDiff && previousRun.result && (
          <div className={`mt-8 ${opt.cardP6}`}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Comparaison des placements</h2>
            <PlacementDiff previousRun={previousRun} />
          </div>
        )}

        {replanMutation.isError && (
          <div className={`mt-6 ${opt.alertError}`} role="alert">
            <p className={opt.alertErrorTitle}>Erreur lors de la replanification</p>
            <p className={opt.alertErrorText}>
              {(replanMutation.error as Error)?.message || "Une erreur inconnue s'est produite"}
            </p>
          </div>
        )}

        {replanMutation.isPending && (
          <div className={`mt-6 ${opt.alertRunning}`} role="status">
            <div className={opt.spinnerSm} />
            <p className={opt.alertRunningTitle}>Replanification en cours...</p>
          </div>
        )}
      </div>
    </div>
  )
}
